import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3, Thermometer } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { LandingShell } from "../components/LandingShell";
import { apiJson } from "../lib/api";

type TemperatureProvider = {
  id: string;
  name: string;
  description: string;
  href: string;
};

const PROVIDERS: TemperatureProvider[] = [
  {
    id: "blulog",
    name: "Blulog",
    description: "Visualiser les données BluConsole et piloter l’extraction depuis MinIO.",
    href: "/temperature/blulog",
  },
];

export function TemperatureProvidersPage() {
  const accessQuery = useQuery({
    queryKey: ["blulog", "access"],
    queryFn: () => apiJson<{ canViewBlulog: boolean }>("/api/blulog/access"),
    staleTime: 60_000,
  });
  const providers = accessQuery.data?.canViewBlulog ? PROVIDERS : [];

  return (
    <LandingShell active="temperature" breadcrumb="Température" searchPlaceholder="Rechercher fournisseurs...">
      <div className="page-container">
        <Link to="/" className="back-link" aria-label="Retour à l’accueil">
          <ArrowLeft size={18} aria-hidden="true" />
          Retour à l'accueil
        </Link>

        <header className="page-header">
          <div className="page-header-content">
            <h1 className="clients-page-title">Espace Température</h1>
            <p className="page-subtitle">
              Choisissez un fournisseur pour accéder à son dashboard dédié.
            </p>
          </div>
        </header>

        <section className="stats-grid" aria-label="Statistiques température">
          <article className="stat-card">
            <div className="stat-card-header">
              <div>
                <p className="stat-label">Fournisseurs</p>
                <p className="stat-value">{providers.length}</p>
              </div>
              <span className="stat-card-icon" aria-hidden="true">
                <Thermometer size={22} />
              </span>
            </div>
          </article>
          <article className="stat-card">
            <div className="stat-card-header">
              <div>
                <p className="stat-label">Dashboards</p>
                <p className="stat-value">{providers.length}</p>
              </div>
              <span className="stat-card-icon" aria-hidden="true">
                <BarChart3 size={22} />
              </span>
            </div>
          </article>
        </section>

        <section className="clients-grid" aria-label="Fournisseurs">
          {providers.map((provider) => (
            <article key={provider.id} className="client-card-compact">
              <div className="client-card-compact-header">
                <div className="client-card-title-section">
                  <Link to={provider.href} className="client-card-link" aria-label={`Ouvrir ${provider.name}`}>
                    <h2 className="client-card-name">{provider.name}</h2>
                  </Link>
                </div>
                <span className="badge badge-blue badge-lg">Dashboard</span>
              </div>

              <div className="client-datasets-section">
                <p className="section-label">Accès disponible</p>
                <div className="datasets-list">
                  <Link to={provider.href} className="dataset-list-item">
                    <div className="dataset-list-info">
                      <strong className="dataset-list-name">{provider.name}</strong>
                      <span className="dataset-list-meta">{provider.description}</span>
                    </div>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </section>

        {accessQuery.isLoading ? <p className="page-subtitle">Chargement des accès…</p> : null}
        {accessQuery.isError ? <p className="cdv-error">Impossible de vérifier les accès BluLog.</p> : null}
        {!accessQuery.isLoading && !accessQuery.isError && providers.length === 0 ? (
          <p className="page-subtitle">Aucun fournisseur température ne vous est actuellement accessible.</p>
        ) : null}
      </div>
    </LandingShell>
  );
}
